import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import { QRCodeCanvas } from "qrcode.react";
import {
  useGetTablesQuery,
  useCreateTableMutation,
  useUpdateTableMutation,
  useGetFloorsQuery,
  useGetSectionsQuery,
} from "../../store/api";
import { useStoreFeatures } from "../../hooks/useStoreFeatures";
import { hasScreenAccess } from "../../utils/permissions";

const Tables = () => {
  const [selectedFloorId, setSelectedFloorId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const user = useSelector((state) => state.auth.user);
  const {
    features,
    isLoading: featuresLoading,
    isError: featuresError,
    refetch: refetchFeatures,
  } = useStoreFeatures(true);
  const [form, setForm] = useState({
    floorId: "",
    sectionId: "",
    tableNumber: "",
    capacity: "",
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [qrModal, setQrModal] = useState({
    isOpen: false,
    tableNo: "",
    qrPublicUrl: "",
  });
  const [localError, setLocalError] = useState("");
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });
  const qrCanvasContainerRef = useRef(null);

  const { data: floors } = useGetFloorsQuery();
  const { data: sections } = useGetSectionsQuery(selectedFloorId, {
    skip: !selectedFloorId,
  });
  const { data: formSections } = useGetSectionsQuery(form.floorId, {
    skip: !form.floorId || !showForm,
  });
  const {
    data: tables,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetTablesQuery(selectedSectionId, {
    skip: !selectedSectionId,
  });

  const [createTable, createState] = useCreateTableMutation();
  const [updateTable, updateState] = useUpdateTableMutation();

  if (featuresLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Tables" subtitle="Loading store features..." />
        <Card>
          <Alert tone="info">Loading store features...</Alert>
        </Card>
      </div>
    );
  }

  if (featuresError || !features) {
    return (
      <div className="space-y-6">
        <PageHeader title="Tables" subtitle="Unable to load store features." />
        <Card className="space-y-3">
          <Alert tone="error">Failed to load store features.</Alert>
          <Button size="sm" onClick={() => refetchFeatures && refetchFeatures()}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  const canViewQr =
    Boolean(features?.enableQr) && hasScreenAccess(user, "FLOOR");

  const floorsList = Array.isArray(floors)
    ? floors.filter((f) => f.isActive !== false)
    : [];
  const sectionsList = Array.isArray(sections)
    ? sections.filter((section) => section?.isActive !== false)
    : [];
  const formSectionsList = Array.isArray(formSections)
    ? formSections.filter((section) => section?.isActive !== false)
    : [];
  const tablesList = Array.isArray(tables) ? tables : [];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFloorChange = (e) => {
    setSelectedFloorId(e.target.value);
    setSelectedSectionId("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    try {
      const payload = {
        sectionId: form.sectionId,
        tableNo: form.tableNumber,
        capacity: Number(form.capacity),
        isActive: form.isActive,
      };
      if (editingId) {
        await updateTable({
          tableId: editingId,
          payload,
        }).unwrap();
      } else {
        await createTable(payload).unwrap();
      }

      if (editingId) {
        setTimeout(() => {
          updateState.reset();
          createState.reset();
          setEditingId(null);
          setForm({
            floorId: "",
            sectionId: "",
            tableNumber: "",
            capacity: "",
            isActive: true,
          });
          setShowForm(false);
        }, 1000);
      } else {
        setForm({
          floorId: "",
          sectionId: "",
          tableNumber: "",
          capacity: "",
          isActive: true,
        });
        setShowForm(false);
        createState.reset();
        updateState.reset();
      }
      refetch();
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (table) => {
    createState.reset();
    updateState.reset();
    setLocalError("");
    const matchedSection = sectionsList.find(
      (s) => s.sectionId === table.sectionId,
    );
    setEditingId(table.tableId);
    setForm({
      floorId:
        table.floorId || matchedSection?.floorId || selectedFloorId || "",
      sectionId: table.sectionId || "",
      tableNumber: table.tableNo || table.tableNumber || "",
      capacity: table.capacity || "",
      isActive: table.isActive !== undefined ? table.isActive : true,
    });
    setShowForm(true);
  };

  const handleShowQr = (table) => {
    const qrUrl = table?.qrPublicUrl || "";
    const tableNo = table?.tableNo || table?.tableNumber || "Table";
    if (!qrUrl) return;

    setQrModal({
      isOpen: true,
      tableNo,
      qrPublicUrl: qrUrl,
    });
  };

  const handleDownloadQr = () => {
    if (!qrCanvasContainerRef.current) return;
    const canvas = qrCanvasContainerRef.current.querySelector("canvas");
    if (!canvas) return;

    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    const safeTableNo = (qrModal.tableNo || "table").replace(/[^\w-]/g, "_");
    link.download = `${safeTableNo}-qr.png`;
    link.click();
  };

  const handleToggleActive = async (table) => {
    setLocalError("");
    try {
      await updateTable({
        tableId: table.tableId,
        payload: {
          sectionId: table.sectionId,
          tableNo: table.tableNo || table.tableNumber || "",
          capacity: Number(table.capacity) || 0,
          isActive: !table.isActive,
        },
      }).unwrap();
      refetch();
    } catch (err) {
      setLocalError(
        err?.data?.detail ||
          err?.data?.message ||
          "Failed to update table status.",
      );
    }
  };

  const handleCancel = () => {
    createState.reset();
    updateState.reset();
    setLocalError("");
    setEditingId(null);
    setForm({
      floorId: "",
      sectionId: "",
      tableNumber: "",
      capacity: "",
      isActive: true,
    });
    setShowForm(false);
  };

  const columns = [
    { key: "tableNumber", label: "Table Number" },
    { key: "section", label: "Section" },
    { key: "capacity", label: "Capacity" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = tablesList.map((table) => {
    const section = sectionsList.find((s) => s.sectionId === table.sectionId);
    return {
      id: table.tableId,
      tableNumber: table.tableNo || table.tableNumber,
      section: section?.name || "—",
      capacity: table.capacity || "—",
      status: (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {table.isActive ? "Active" : "Inactive"}
        </span>
      ),
      actions: (
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="p-1 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition"
            onClick={() => handleEdit(table)}
            title="Edit"
            aria-label="Edit table"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <input
            type="checkbox"
            checked={Boolean(table.isActive)}
            onChange={() => handleToggleActive(table)}
            disabled={updateState.isLoading}
            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800"
            aria-label={
              table.isActive
                ? `Mark ${table.tableNo || table.tableNumber} inactive`
                : `Mark ${table.tableNo || table.tableNumber} active`
            }
          />
          {canViewQr && (
            <button
              type="button"
              className="p-1 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleShowQr(table)}
              title={table.qrPublicUrl ? "View QR" : "QR unavailable"}
              aria-label="View table QR"
              disabled={!table.qrPublicUrl}
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h2v2h-2v-2zm4 0h2v2h-2v-2zm-4 4h2v2h-2v-2zm4 0h2v2h-2v-2z"
                />
              </svg>
            </button>
          )}
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tables"
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => {
                createState.reset();
                updateState.reset();
                setLocalError("");
                setEditingId(null);
                setForm({
                  floorId: "",
                  sectionId: "",
                  tableNumber: "",
                  capacity: "",
                  isActive: true,
                });
                setShowForm(true);
              }}
            >
              Add Table
            </Button>
          )
        }
      />

      {!showForm && (
        <Card className="p-0">
          {/* Filter Section */}
          <div className="border-b border-slate-100 dark:border-slate-700 px-6 py-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Select
                label="Select Floor"
                value={selectedFloorId}
                onChange={handleFloorChange}
              >
                <option value="">Select a floor</option>
                {floorsList.map((floor) => (
                  <option key={floor.floorId} value={floor.floorId}>
                    {floor.name}
                  </option>
                ))}
              </Select>

              <Select
                label="Select Section"
                value={selectedSectionId}
                onChange={(e) => setSelectedSectionId(e.target.value)}
                disabled={!selectedFloorId}
              >
                <option value="">Select a section</option>
                {sectionsList.map((section) => (
                  <option key={section.sectionId} value={section.sectionId}>
                    {section.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Table Section */}
          {selectedSectionId ? (
            <div className="px-6 py-4">
              {localError && <Alert tone="error">{localError}</Alert>}
              {isLoading && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Loading tables...
                </p>
              )}
              {isError && (
                <Alert tone="error">
                  {error?.data?.detail || "Failed to load tables."}
                </Alert>
              )}
              {!isLoading && !isError && (
                <Table
                  columns={columns}
                  rows={rows}
                  emptyText="No tables yet."
                />
              )}
            </div>
          ) : (
            <div className="px-6 py-4">
              <Alert tone="info">
                Please select a floor and section to view tables.
              </Alert>
            </div>
          )}
        </Card>
      )}

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingId ? "Edit Table" : "Add Table"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <Select
              label="Floor"
              name="floorId"
              value={form.floorId}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  floorId: e.target.value,
                  sectionId: "",
                }))
              }
              required
            >
              <option value="">Select a floor</option>
              {floorsList.map((floor) => (
                <option key={floor.floorId} value={floor.floorId}>
                  {floor.name}
                </option>
              ))}
            </Select>

            <Select
              label="Section"
              name="sectionId"
              value={form.sectionId}
              onChange={handleChange}
              disabled={!form.floorId}
              required
            >
              <option value="">Select a section</option>
              {formSectionsList.map((section) => (
                <option key={section.sectionId} value={section.sectionId}>
                  {section.name}
                </option>
              ))}
            </Select>

            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Table Number"
                name="tableNumber"
                placeholder="T1"
                value={form.tableNumber}
                onChange={handleChange}
                required
              />
              <Input
                label="Capacity"
                name="capacity"
                type="number"
                placeholder="4"
                value={form.capacity}
                onChange={handleChange}
                required
              />
              <Checkbox
                label="Active"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
              />
            </div>

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Table {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save table."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Table
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      {canViewQr && (
        <Modal
          isOpen={qrModal.isOpen}
          onClose={() =>
            setQrModal({ isOpen: false, tableNo: "", qrPublicUrl: "" })
          }
          title={`Table ${qrModal.tableNo} QR`}
          confirmText="Download PNG"
          cancelText="Close"
          confirmVariant="primary"
          onConfirm={handleDownloadQr}
        >
          <div className="space-y-4">
            <div
              ref={qrCanvasContainerRef}
              className="mx-auto flex w-fit items-center justify-center rounded-lg border border-slate-200 bg-white p-3"
            >
              {qrModal.qrPublicUrl ? (
                <QRCodeCanvas
                  value={qrModal.qrPublicUrl}
                  size={240}
                  level="M"
                  includeMargin
                />
              ) : (
                <p className="text-sm text-slate-500">QR URL unavailable.</p>
              )}
            </div>
            {/* <p className="break-all rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-900/40 dark:text-slate-300">
              {qrModal.qrPublicUrl}
            </p> */}
          </div>
        </Modal>
      )}

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default Tables;
